// aifab - CompareModal
import { useState, useEffect } from 'react';
import { apiFetch } from '../../hooks/apiFetch';
import { Modal, Err, Spinner, ModalBody, ModalFooter } from '../common';

export function CompareModal({ runA, runB, onClose }) {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [err,     setErr]     = useState(null);

  useEffect(() => {
    apiFetch(`/runs/${runA.id}/compare?compare_with=${runB.id}&primary_metric=f1_score`)
      .then(d  => { setData(d); setLoading(false); })
      .catch(e => { setErr(e.message); setLoading(false); });
  }, [runA.id, runB.id]);

  const deltaClass = (d) => d == null ? 'delta-neu' : d > 0 ? 'delta-pos' : d < 0 ? 'delta-neg' : 'delta-neu';
  const deltaStr   = (d) => d == null ? '-' : d === 0 ? '=' : d > 0
    ? `+${(d * 100).toFixed(2)}%`
    : `${(d * 100).toFixed(2)}%`;

  const betterNum = data?.summary.better_run === 'B' ? runB.run_number : runA.run_number;

  return (
    <Modal title={`Compare: Run #${runA.run_number} vs #${runB.run_number}`} onClose={onClose} wide>
      <ModalBody>
      {loading ? (
        <div style={{ textAlign: 'center', padding: 32 }}><Spinner /></div>
      ) : err ? <Err msg={err} /> : data && (
        <>
          {data.summary.better_run !== 'tie' && (
            <div className={`alert ${data.summary.better_run === 'B' ? 'alert-ok' : 'alert-warn'}`}>
              <strong>Run #{betterNum}</strong> performs better on {data.primary_metric}
              {data.summary.delta != null && <span> (Δ {deltaStr(data.summary.delta)})</span>}
            </div>
          )}

          {/* Metrics table */}
          <div className="sm c-sl mb8" style={{ letterSpacing: '.1em', textTransform: 'uppercase', fontSize: 9 }}>Metrics</div>
          <table className="compare-table mb20">
            <thead>
              <tr>
                <th>Metric</th>
                <th>
                  Run #{runA.run_number}
                  {runA.run_label && <span className="c-sl"> ({runA.run_label})</span>}
                </th>
                <th>
                  Run #{runB.run_number}
                  {runB.run_label && <span className="c-sl"> ({runB.run_label})</span>}
                </th>
                <th>Δ</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(data.metrics_diff).map(([metric, d]) =>
                d.A != null || d.B != null ? (
                  <tr key={metric}>
                    <td className="c-sl" style={{ textTransform: 'uppercase', fontSize: 10, letterSpacing: '.08em' }}>{metric}</td>
                    <td>{d.A != null ? (d.A * 100).toFixed(2) + '%' : '-'}</td>
                    <td>{d.B != null ? (d.B * 100).toFixed(2) + '%' : '-'}</td>
                    <td className={deltaClass(d.delta)}>
                      {d.delta != null ? (d.delta > 0 ? '+' : '') + (d.delta * 100).toFixed(2) + '%' : '-'}
                    </td>
                  </tr>
                ) : null
              )}
            </tbody>
          </table>

          {/* Changed params */}
          {Object.keys(data.params_diff.changed || {}).length > 0 && (
            <>
              <div className="sm c-sl mb8" style={{ letterSpacing: '.1em', textTransform: 'uppercase', fontSize: 9 }}>Changed Params</div>
              <table className="compare-table mb20">
                <thead><tr><th>Param</th><th>Run A</th><th>Run B</th></tr></thead>
                <tbody>
                  {Object.entries(data.params_diff.changed || {}).map(([k, v]) => (
                    <tr key={k}>
                      <td className="mono c-sl">{k}</td>
                      <td className="mono">{String(v.A)}</td>
                      <td className="mono c-ylw">{String(v.B)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}

          {/* Feature diff */}
          {(data.feature_diff.added?.length > 0 || data.feature_diff.removed?.length > 0) && (
            <>
              <div className="sm c-sl mb8" style={{ letterSpacing: '.1em', textTransform: 'uppercase', fontSize: 9 }}>Feature Changes</div>
              <div className="flex-row mb16" style={{ flexWrap: 'wrap', gap: 6 }}>
                {data.feature_diff.added?.map(f   => <span key={f} className="badge b-green diff-added">+ {f}</span>)}
                {data.feature_diff.removed?.map(f => <span key={f} className="badge b-red diff-removed">- {f}</span>)}
              </div>
            </>
          )}
        </>
      )}
      </ModalBody>
      <ModalFooter>
        <button className="btn btn-ghost btn-sm" onClick={onClose}>Close</button>
      </ModalFooter>
    </Modal>
  );
}
